/**
 * Read-only Power Platform inventory client.
 *
 * Copilot Studio's Dataverse tables answer only for agents the current maker
 * can read in one environment.  The inventory API is the supported tenant-wide
 * view used by the Power Platform admin center.  It is eventually consistent
 * and describes the published agent configuration, not an unpublished draft.
 */

import type { AuthProvider } from "../auth/types.js";
import { RESOURCES } from "../auth/types.js";
import {
  ApiError,
  AuthenticationError,
  AuthorizationError,
  RateLimitError,
  ValidationError,
  wrapApiCall,
} from "../utils/errors.js";
import { requestWithRetry, readResponseBody } from "../utils/http.js";
import { GUID_PATTERN, sanitizeErrorMessage } from "../utils/security.js";

export const POWER_PLATFORM_INVENTORY_ORIGIN = "https://api.powerplatform.com";
export const POWER_PLATFORM_INVENTORY_API_VERSION = "2024-10-01";
export const COPILOT_AGENT_RESOURCE_TYPE = "microsoft.copilotstudio/agents";

/** Keep one tool call bounded even in very large tenants. */
export const COPILOT_INVENTORY_PAGE_SIZE = 100;
export const COPILOT_INVENTORY_MAX_PAGES = 5;
export const COPILOT_INVENTORY_DEFAULT_LIMIT = 100;
export const COPILOT_INVENTORY_MAX_LIMIT =
  COPILOT_INVENTORY_PAGE_SIZE * COPILOT_INVENTORY_MAX_PAGES;

const MAX_INVENTORY_RESPONSE_BYTES = 10 * 1024 * 1024;
const MAX_CONNECTORS_PER_AGENT = 200;
const MAX_CONNECTOR_OPERATIONS_PER_AGENT = 200;

export interface PowerPlatformInventoryApiConfig {
  authProvider: AuthProvider;
}

export interface CopilotInventorySharingCounts {
  userCount?: number;
  groupCount?: number;
  entireTenant?: boolean;
}

export interface CopilotInventoryCapabilitiesCounts {
  distinctPowerPlatformConnectors?: number;
  distinctPowerPlatformConnectorsOperations?: number;
  flows?: number;
}

export interface CopilotInventoryComponentCounts {
  topics?: number;
  tools?: number;
  knowledge?: number;
  connectedAgents?: number;
  knowledgeByType?: Record<string, number>;
}

/**
 * Connector-operation metadata safe to return from the MCP tool.
 *
 * The raw inventory also contains creator and maker-connection GUIDs.  Those
 * identity-bearing fields are intentionally reduced to `hasMakerConnection`.
 */
export interface CopilotInventoryConnectorOperation {
  operationId?: string;
  type?: string;
  usedAs?: string;
  isEnabled?: boolean;
  requiresEndUserConsent?: boolean;
  whenCanBeUsed?: string;
  connectionProvider?: string;
  hasMakerConnection?: boolean;
}

export interface CopilotInventoryConnector {
  connectorId?: string;
  operations: CopilotInventoryConnectorOperation[];
}

/** Sanitized subset of one Copilot Studio inventory resource. */
export interface CopilotAgentInventoryRecord {
  agentId: string;
  displayName?: string;
  environmentId?: string;
  /** Entra object ID of the current owner, validated as a GUID. */
  ownerId?: string;
  /** Entra object ID of the creator, validated as a GUID. */
  createdBy?: string;
  createdAt?: string;
  lastPublishedAt?: string;
  createdIn?: string;
  schemaName?: string;
  isQuarantined?: boolean;
  quarantinedAt?: string;
  isManaged?: boolean;
  orchestration?: string;
  model?: string;
  authentication?: string;
  channels: string[];
  sharedWithViewers?: CopilotInventorySharingCounts;
  sharedWithEditors?: CopilotInventorySharingCounts;
  capabilitiesCounts?: CopilotInventoryCapabilitiesCounts;
  componentsCounts?: CopilotInventoryComponentCounts;
  instructionsCharactersCount?: number;
  isWebSearchEnabledForKnowledge?: boolean;
  isCLIAgent?: boolean;
  isGithubCopilotAgent?: boolean;
  powerPlatformConnectors: CopilotInventoryConnector[];
  /** True when counts or local safety bounds prove the returned connector detail is incomplete. */
  connectorDetailsIncomplete: boolean;
}

export interface QueryCopilotAgentInventoryOptions {
  /** Exact Dataverse bot GUID. Omit for every inventory agent visible to the caller's tenant role. */
  agentId?: string;
  /** Maximum sanitized records returned by this call (default 100, maximum 500). */
  limit?: number;
  /** Opaque continuation returned by a previous call. */
  skipToken?: string;
}

export interface CopilotAgentInventoryResult {
  agents: CopilotAgentInventoryRecord[];
  count: number;
  totalRecords?: number;
  pagesFetched: number;
  truncated: boolean;
  nextSkipToken: string | null;
}

interface ResourceQueryResponse {
  totalRecords?: unknown;
  count?: unknown;
  /** 0 = truncated, 1 = not truncated in the published API contract. */
  resultTruncated?: unknown;
  skipToken?: unknown;
  data?: unknown;
}

interface ResourceQueryRequest {
  TableName: "PowerPlatformResources";
  Clauses: Array<Record<string, unknown>>;
  Options: {
    Top: number;
    Skip?: number;
    SkipToken: string;
  };
}

function asObject(value: unknown): Record<string, unknown> | undefined {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value !== "string") return undefined;
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}

/** Single-line, bounded text for model-facing output. */
function safeString(value: unknown, maxLength = 500): string | undefined {
  if (typeof value !== "string") return undefined;
  const cleaned = Array.from(value, (character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || codePoint === 0x7f ? " " : character;
  })
    .join("")
    .trim();
  if (!cleaned) return undefined;
  return cleaned.slice(0, maxLength);
}

function safeBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function safeGuid(value: unknown): string | undefined {
  const candidate = safeString(value, 100);
  return candidate && GUID_PATTERN.test(candidate) ? candidate.toLowerCase() : undefined;
}

function safeCount(value: unknown): number | undefined {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : undefined;
}

/** Continuation tokens are opaque: validate their bound, but never trim or rewrite them. */
function continuationToken(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 && value.length <= 8_192 ? value : undefined;
}

function sharingCounts(value: unknown): CopilotInventorySharingCounts | undefined {
  const raw = asObject(value);
  if (!raw) return undefined;
  const result: CopilotInventorySharingCounts = {};
  const userCount = safeCount(raw.userCount);
  const groupCount = safeCount(raw.groupCount);
  const entireTenant = safeBoolean(raw.entireTenant);
  if (userCount !== undefined) result.userCount = userCount;
  if (groupCount !== undefined) result.groupCount = groupCount;
  if (entireTenant !== undefined) result.entireTenant = entireTenant;
  return Object.keys(result).length > 0 ? result : undefined;
}

function capabilitiesCounts(value: unknown): CopilotInventoryCapabilitiesCounts | undefined {
  const raw = asObject(value);
  if (!raw) return undefined;
  const result: CopilotInventoryCapabilitiesCounts = {};
  const connectors = safeCount(raw.distinctPowerPlatformConnectors);
  const operations = safeCount(raw.distinctPowerPlatformConnectorsOperations);
  const flows = safeCount(raw.flows);
  if (connectors !== undefined) result.distinctPowerPlatformConnectors = connectors;
  if (operations !== undefined) result.distinctPowerPlatformConnectorsOperations = operations;
  if (flows !== undefined) result.flows = flows;
  return Object.keys(result).length > 0 ? result : undefined;
}

function componentCounts(value: unknown): CopilotInventoryComponentCounts | undefined {
  const raw = asObject(value);
  if (!raw) return undefined;
  const result: CopilotInventoryComponentCounts = {};
  const topics = safeCount(raw.topics);
  const tools = safeCount(raw.tools);
  const knowledge = safeCount(raw.knowledge);
  const connectedAgents = safeCount(raw.connectedAgents);
  if (topics !== undefined) result.topics = topics;
  if (tools !== undefined) result.tools = tools;
  if (knowledge !== undefined) result.knowledge = knowledge;
  if (connectedAgents !== undefined) result.connectedAgents = connectedAgents;

  const rawKnowledgeByType = asObject(raw.knowledgeByType);
  if (rawKnowledgeByType) {
    const safeKnowledgeByType: Record<string, number> = {};
    for (const [rawKey, rawValue] of Object.entries(rawKnowledgeByType).slice(0, 50)) {
      const key = safeString(rawKey, 100);
      const count = safeCount(rawValue);
      if (key && count !== undefined) safeKnowledgeByType[key] = count;
    }
    if (Object.keys(safeKnowledgeByType).length > 0) {
      result.knowledgeByType = safeKnowledgeByType;
    }
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

function connectorOperations(
  value: unknown,
  limit: number,
): { operations: CopilotInventoryConnectorOperation[]; truncated: boolean; consumed: number } {
  if (!Array.isArray(value)) return { operations: [], truncated: false, consumed: 0 };
  const candidates = value.slice(0, limit);
  const operations = candidates.flatMap((candidate) => {
    const raw = asObject(candidate);
    if (!raw) return [];
    const operation: CopilotInventoryConnectorOperation = {};
    const operationId = safeString(raw.operationId ?? raw.OperationId, 300);
    const type = safeString(raw.type, 100);
    const usedAs = safeString(raw.usedAs, 100);
    const isEnabled = safeBoolean(raw.isEnabled);
    const requiresEndUserConsent = safeBoolean(raw.requiresEndUserConsent);
    const whenCanBeUsed = safeString(raw.whenCanBeUsed, 100);
    const connectionProvider = safeString(raw.connectionProvider, 100);
    if (operationId !== undefined) operation.operationId = operationId;
    if (type !== undefined) operation.type = type;
    if (usedAs !== undefined) operation.usedAs = usedAs;
    if (isEnabled !== undefined) operation.isEnabled = isEnabled;
    if (requiresEndUserConsent !== undefined) {
      operation.requiresEndUserConsent = requiresEndUserConsent;
    }
    if (whenCanBeUsed !== undefined) operation.whenCanBeUsed = whenCanBeUsed;
    if (connectionProvider !== undefined) operation.connectionProvider = connectionProvider;
    if (raw.connectionIdSharedByMaker !== undefined) {
      operation.hasMakerConnection = safeString(raw.connectionIdSharedByMaker, 200) !== undefined;
    }
    return [operation];
  });
  return {
    operations,
    truncated: value.length > candidates.length,
    consumed: candidates.length,
  };
}

function connectors(value: unknown): {
  connectors: CopilotInventoryConnector[];
  truncated: boolean;
} {
  if (!Array.isArray(value)) return { connectors: [], truncated: false };
  const candidates = value.slice(0, MAX_CONNECTORS_PER_AGENT);
  let remainingOperations = MAX_CONNECTOR_OPERATIONS_PER_AGENT;
  let truncated = value.length > candidates.length;
  const sanitized = candidates.flatMap((candidate) => {
    const raw = asObject(candidate);
    if (!raw) return [];
    const operationResult = connectorOperations(raw.operations, remainingOperations);
    remainingOperations -= operationResult.consumed;
    truncated ||= operationResult.truncated;
    return [
      {
        connectorId: safeString(raw.connectorId, 500),
        operations: operationResult.operations,
      },
    ];
  });
  return { connectors: sanitized, truncated };
}

/**
 * Drop unbounded/free-form properties from an ARG row and validate identity
 * metadata as GUIDs. Exported for a focused regression test and future tools.
 */
export function sanitizeCopilotInventoryRecord(value: unknown): CopilotAgentInventoryRecord | null {
  const raw = asObject(value);
  if (!raw) return null;
  const properties = asObject(raw.properties) ?? {};
  const agentId = safeGuid(raw.name ?? properties.name ?? properties.botId);
  if (!agentId) return null;
  const connectorDetails = connectors(properties.powerPlatformConnectors);

  const record: CopilotAgentInventoryRecord = {
    agentId,
    channels: Array.isArray(properties.channels)
      ? properties.channels
          .slice(0, 50)
          .map((channel) => safeString(channel, 200))
          .filter((channel): channel is string => channel !== undefined)
      : [],
    powerPlatformConnectors: connectorDetails.connectors,
    connectorDetailsIncomplete: connectorDetails.truncated,
  };

  const strings: Array<[keyof CopilotAgentInventoryRecord, unknown, number?]> = [
    ["displayName", properties.displayName],
    ["environmentId", properties.environmentId, 100],
    ["createdAt", properties.createdAt, 100],
    ["lastPublishedAt", properties.lastPublishedAt, 100],
    ["createdIn", properties.createdIn],
    ["schemaName", properties.schemaName],
    ["quarantinedAt", properties.quarantinedAt, 100],
    ["orchestration", properties.orchestration],
    ["model", properties.model],
    ["authentication", properties.authentication],
  ];
  for (const [key, rawValue, maxLength] of strings) {
    const safe = safeString(rawValue, maxLength);
    if (safe !== undefined) {
      (record as unknown as Record<string, unknown>)[key] = safe;
    }
  }

  const booleans: Array<[keyof CopilotAgentInventoryRecord, unknown]> = [
    ["isQuarantined", properties.isQuarantined],
    ["isManaged", properties.isManaged],
    [
      "isWebSearchEnabledForKnowledge",
      properties.isWebSearchEnabledForKnowledge ?? properties.IsWebSearchEnabledForKnowledge,
    ],
    ["isCLIAgent", properties.isCLIAgent],
    ["isGithubCopilotAgent", properties.isGithubCopilotAgent],
  ];
  for (const [key, rawValue] of booleans) {
    const safe = safeBoolean(rawValue);
    if (safe !== undefined) {
      (record as unknown as Record<string, unknown>)[key] = safe;
    }
  }

  const viewers = sharingCounts(properties.sharedWithViewers);
  const editors = sharingCounts(properties.sharedWithEditors);
  const capabilityCounts = capabilitiesCounts(properties.capabilitiesCounts);
  const components = componentCounts(properties.componentsCounts);
  const instructionCharacters = safeCount(properties.instructionsCharactersCount);
  const ownerId = safeGuid(properties.ownerId);
  const createdBy = safeGuid(properties.createdBy);
  if (ownerId) record.ownerId = ownerId;
  if (createdBy) record.createdBy = createdBy;
  if (viewers) record.sharedWithViewers = viewers;
  if (editors) record.sharedWithEditors = editors;
  if (capabilityCounts) record.capabilitiesCounts = capabilityCounts;
  if (components) record.componentsCounts = components;
  if (instructionCharacters !== undefined) {
    record.instructionsCharactersCount = instructionCharacters;
  }

  if (capabilityCounts) {
    const returnedOperations = record.powerPlatformConnectors.reduce(
      (sum, connector) => sum + connector.operations.length,
      0,
    );
    record.connectorDetailsIncomplete ||=
      (capabilityCounts.distinctPowerPlatformConnectors !== undefined &&
        capabilityCounts.distinctPowerPlatformConnectors > record.powerPlatformConnectors.length) ||
      (capabilityCounts.distinctPowerPlatformConnectorsOperations !== undefined &&
        capabilityCounts.distinctPowerPlatformConnectorsOperations > returnedOperations);
  }

  return record;
}

function validateOptions(options: QueryCopilotAgentInventoryOptions): {
  agentId?: string;
  limit: number;
  skipToken?: string;
} {
  const agentId = options.agentId?.trim();
  if (agentId && !GUID_PATTERN.test(agentId)) {
    throw new ValidationError("agentId must be a GUID");
  }
  if (agentId && options.skipToken) {
    throw new ValidationError("skipToken cannot be combined with agentId");
  }

  const limit = options.limit ?? COPILOT_INVENTORY_DEFAULT_LIMIT;
  if (!Number.isInteger(limit) || limit < 1 || limit > COPILOT_INVENTORY_MAX_LIMIT) {
    throw new ValidationError(`limit must be an integer from 1 to ${COPILOT_INVENTORY_MAX_LIMIT}`);
  }

  const skipToken = options.skipToken;
  if (skipToken !== undefined && (skipToken.length === 0 || skipToken.length > 8_192)) {
    throw new ValidationError("skipToken must contain 1 to 8192 characters");
  }

  return {
    ...(agentId ? { agentId } : {}),
    limit: agentId ? 1 : limit,
    ...(skipToken ? { skipToken } : {}),
  };
}

function queryClauses(agentId?: string): Array<Record<string, unknown>> {
  const clauses: Array<Record<string, unknown>> = [
    {
      $type: "where",
      FieldName: "type",
      Operator: "==",
      Values: [`'${COPILOT_AGENT_RESOURCE_TYPE}'`],
    },
  ];
  if (agentId) {
    clauses.push({
      $type: "where",
      FieldName: "name",
      Operator: "==",
      Values: [`'${agentId}'`],
    });
  } else {
    clauses.push({
      $type: "orderby",
      FieldNamesAscDesc: { "tostring(properties.createdAt)": "desc" },
    });
  }
  return clauses;
}

/** Client for the official Power Platform Resource Query inventory endpoint. */
export class PowerPlatformInventoryApi {
  private readonly authProvider: AuthProvider;

  constructor(config: PowerPlatformInventoryApiConfig) {
    this.authProvider = config.authProvider;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.authProvider.getTokenForResource(RESOURCES.POWER_PLATFORM_API);
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    };
  }

  private async requestPage(request: ResourceQueryRequest): Promise<ResourceQueryResponse> {
    const headers = await this.getAuthHeaders();
    const url =
      `${POWER_PLATFORM_INVENTORY_ORIGIN}/resourcequery/resources/query` +
      `?api-version=${POWER_PLATFORM_INVENTORY_API_VERSION}`;

    const response = await requestWithRetry(
      url,
      { method: "POST", headers, body: JSON.stringify(request) },
      // POST is a read-only resource query and is safe to retry after throttling.
      { retryNonIdempotent: true },
    );

    if (response.statusCode === 429) {
      const retryAfter = response.headers["retry-after"];
      await response.body.text().catch(() => undefined);
      const seconds = retryAfter
        ? Number(Array.isArray(retryAfter) ? retryAfter[0] : retryAfter)
        : NaN;
      throw new RateLimitError(Number.isFinite(seconds) ? seconds * 1000 : undefined);
    }

    const { statusCode, text } = await readResponseBody(
      response,
      "Query Copilot Studio agent inventory",
      MAX_INVENTORY_RESPONSE_BYTES,
    );

    if (statusCode === 401) {
      throw new AuthenticationError(
        "Copilot Studio inventory authentication failed. The app registration needs the " +
          "Power Platform API delegated permission ResourceQuery.Resources.Read with admin " +
          "consent. Ask a Microsoft Entra administrator to add and consent that permission, then refresh the signed-in session.",
      );
    }
    if (statusCode === 403) {
      if (/InsufficientDelegatedPermissions|ResourceQuery\.Resources\.Read/i.test(text)) {
        throw new AuthorizationError(
          "Copilot Studio inventory is missing the Power Platform API delegated permission " +
            "ResourceQuery.Resources.Read. Grant that permission and admin consent to the app " +
            "registration through a Microsoft Entra administrator, then refresh the signed-in session.",
        );
      }
      throw new AuthorizationError(
        "Copilot Studio tenant inventory access denied. The response does not distinguish a " +
          "missing delegated permission from a missing user role. Verify the app has consented " +
          "ResourceQuery.Resources.Read and refresh the signed-in session. The user also needs one supported " +
          "Microsoft Entra role: AI Reader, AI Administrator, Global Reader, Global Administrator, " +
          "Power Platform Administrator, or Dynamics 365 Administrator. AI Reader is sufficient " +
          "for agent inventory; environment-level Power Platform RBAC roles are not.",
      );
    }
    if (statusCode >= 400) {
      let details: unknown = sanitizeErrorMessage(text);
      try {
        details = JSON.parse(text) as unknown;
      } catch {
        // Keep the already-sanitized text.
      }
      throw new ApiError(
        "Query Copilot Studio agent inventory: Power Platform inventory API error",
        statusCode,
        typeof details === "string" ? details : sanitizeErrorMessage(JSON.stringify(details)),
      );
    }

    if (!text) {
      throw new ApiError(
        "Query Copilot Studio agent inventory: server returned an empty response",
        statusCode,
      );
    }
    try {
      const parsed = JSON.parse(text) as unknown;
      if (!asObject(parsed)) throw new Error("response is not an object");
      return parsed as ResourceQueryResponse;
    } catch (error) {
      throw new ApiError(
        "Query Copilot Studio agent inventory: invalid JSON response",
        statusCode,
        sanitizeErrorMessage(error instanceof Error ? error.message : String(error)),
      );
    }
  }

  /**
   * Get one agent by GUID or a bounded, resumable tenant-wide agent inventory.
   */
  async queryCopilotAgents(
    options: QueryCopilotAgentInventoryOptions = {},
  ): Promise<CopilotAgentInventoryResult> {
    const parsed = validateOptions(options);

    return wrapApiCall(async () => {
      const agents: CopilotAgentInventoryRecord[] = [];
      const seenTokens = new Set<string>();
      const startedFromContinuation = Boolean(parsed.skipToken);
      let nextSkipToken = parsed.skipToken ?? "";
      let pagesFetched = 0;
      let totalRecords: number | undefined;
      let serviceReportedTruncation = false;

      if (nextSkipToken) seenTokens.add(nextSkipToken);

      while (pagesFetched < COPILOT_INVENTORY_MAX_PAGES && agents.length < parsed.limit) {
        const top = Math.min(COPILOT_INVENTORY_PAGE_SIZE, parsed.limit - agents.length);
        const request: ResourceQueryRequest = {
          TableName: "PowerPlatformResources",
          Clauses: queryClauses(parsed.agentId),
          Options: nextSkipToken
            ? { Top: top, SkipToken: nextSkipToken }
            : { Top: top, Skip: 0, SkipToken: "" },
        };
        const page = await this.requestPage(request);
        pagesFetched += 1;

        if (!Array.isArray(page.data)) {
          throw new ApiError(
            "Query Copilot Studio agent inventory: response data was not an array",
            502,
          );
        }

        const reportedTotal = safeCount(page.totalRecords);
        if (reportedTotal !== undefined) totalRecords = reportedTotal;

        const pageRows = page.data;
        if (pageRows.length > top) {
          throw new ApiError(
            `Query Copilot Studio agent inventory: server returned ${pageRows.length} rows for a page bounded to ${top}`,
            502,
          );
        }
        for (const row of pageRows) {
          const sanitized = sanitizeCopilotInventoryRecord(row);
          if (sanitized) agents.push(sanitized);
        }

        const candidateToken = continuationToken(page.skipToken) ?? "";
        serviceReportedTruncation = page.resultTruncated === 0;

        if (parsed.agentId || !candidateToken) {
          nextSkipToken = "";
          break;
        }
        if (seenTokens.has(candidateToken)) {
          // A repeated continuation must never turn this bounded read into a loop.
          nextSkipToken = "";
          serviceReportedTruncation = true;
          break;
        }
        seenTokens.add(candidateToken);
        nextSkipToken = candidateToken;

        if (
          !serviceReportedTruncation &&
          totalRecords !== undefined &&
          agents.length >= totalRecords
        ) {
          nextSkipToken = "";
          break;
        }
      }

      const truncated =
        nextSkipToken.length > 0 ||
        serviceReportedTruncation ||
        (!startedFromContinuation &&
          totalRecords !== undefined &&
          agents.length < totalRecords &&
          !parsed.agentId);

      return {
        agents,
        count: agents.length,
        ...(totalRecords !== undefined ? { totalRecords } : {}),
        pagesFetched,
        truncated,
        nextSkipToken: nextSkipToken || null,
      };
    }, "Query Copilot Studio agent inventory");
  }
}
